/**
 * Top bar (Task 201 extraction; unified across views in Task 224).
 *
 * One nav language everywhere: Wordmark (→ chat home) on the left; the four
 * destinations `Chat · Biblioteca · Conexiones · Caminos` with an active state;
 * then view extras (e.g. the reader's readability controls), About, and language.
 *
 * Variants:
 *   - "idle":    chat home (absolute within the fixed chat shell);
 *   - "chat":    active conversation (adds the New-chat button, mobile);
 *   - "explore": corpus reader + connections (sticky within the scrolling
 *                document shell — `.topbar--explore`).
 */
import type { ChangeEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { Wordmark, BowSigil } from "./Wordmark";
import { chatPath, connectionsPath, corpusPath, caminosPath, softNavigate } from "../corpus/use-corpus-route";
import type { Strings, Language } from "../strings";

/** The four learning destinations (Caminos joined the rail in Task 227). */
export type ActiveView = "chat" | "corpus" | "connections" | "caminos";

interface TopbarProps {
  variant: "idle" | "chat" | "explore";
  /** Which destination is current (drives aria-current + the gold underline). */
  active: ActiveView;
  strings: Strings;
  /** Kept in the contract for the callers, but the picker is HIDDEN for the
   *  Spanish-only initial release (Diego, 2026-07-03). The ?lang=en URL
   *  mechanism still works; restore the LanguageSelect from git history when
   *  the English surface is ready. */
  language: Language;
  onLanguageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onAbout: () => void;
  /** chat variant only — start a new conversation. */
  onNewChat?: () => void;
  /** explore views — extra controls (e.g. ReadabilityControls), before About. */
  extras?: ReactNode;
}

/** The four destinations, shared by the desktop rail and the mobile tab bar. */
function navDestinations(strings: Strings): Array<{ view: ActiveView; label: string; href: string }> {
  return [
    { view: "chat", label: strings.navChat, href: chatPath() },
    { view: "corpus", label: strings.navBiblioteca, href: corpusPath() },
    { view: "connections", label: strings.navConexiones, href: connectionsPath() },
    { view: "caminos", label: strings.navCaminos, href: caminosPath() },
  ];
}

function NavLinks(props: { strings: Strings; active: ActiveView }): JSX.Element {
  const { strings, active } = props;
  return (
    <nav className="topbar__nav" aria-label={strings.appTitle}>
      {navDestinations(strings).map((link) => (
        <a
          key={link.view}
          className={`topbar__nav-link${active === link.view ? " is-active" : ""}`}
          href={link.href}
          aria-current={active === link.view ? "page" : undefined}
          onClick={(e) => {
            e.preventDefault();
            if (active !== link.view || window.location.pathname !== new URL(link.href, window.location.href).pathname) {
              softNavigate(link.href);
            }
          }}
        >
          {link.label}
        </a>
      ))}
    </nav>
  );
}

/** True mobile navigation (release audit, 2026-07-03): a fixed bottom tab bar
 *  at phone widths — the desktop rail never fit four destinations beside the
 *  sigil on a narrow screen. Hidden on desktop via CSS; not rendered at all in
 *  chat-active (the composer owns the bottom edge there) nor in the Caminos
 *  flight (which mounts no Topbar). */
function MobileNav(props: { strings: Strings; active: ActiveView }): JSX.Element {
  const { strings, active } = props;
  return (
    <nav className="mobilenav" aria-label={strings.appTitle}>
      {navDestinations(strings).map((link) => (
        <a
          key={link.view}
          className={`mobilenav__link${active === link.view ? " is-active" : ""}`}
          href={link.href}
          aria-current={active === link.view ? "page" : undefined}
          onClick={(e) => {
            e.preventDefault();
            if (active !== link.view || window.location.pathname !== new URL(link.href, window.location.href).pathname) {
              softNavigate(link.href);
            }
          }}
        >
          {link.label}
        </a>
      ))}
    </nav>
  );
}

export function Topbar(props: TopbarProps): JSX.Element {
  const { variant, active, strings, onAbout, onNewChat, extras } = props;

  return (
    <div className={`topbar topbar--${variant}`}>
      <a
        className="topbar__home"
        href={chatPath()}
        aria-label={strings.appTitle}
        onClick={(e) => {
          e.preventDefault();
          if (active !== "chat") softNavigate(chatPath());
        }}
      >
        <Wordmark strings={strings} />
      </a>
      <div className="topbar__right">
        <NavLinks strings={strings} active={active} />
        {extras}
        <button
          type="button"
          className={`topbar__about${variant === "chat" ? " topbar__about--chat" : ""}`}
          onClick={onAbout}
        >
          {strings.navAbout}
        </button>
        {variant === "chat" ? (
          <button type="button" className="topbar__newchat" onClick={onNewChat}>
            <BowSigil className="" />
            {strings.threadNewChat}
          </button>
        ) : null}
      </div>
      {/* PORTALED to <body>: .topbar--explore's backdrop-filter creates a
          containing block that would capture a position:fixed child and pin
          the bar to the topbar instead of the viewport. */}
      {variant !== "chat" ? createPortal(<MobileNav strings={strings} active={active} />, document.body) : null}
    </div>
  );
}
